/**
 * CaptionBuilderSettings - Manage caption builder configuration
 *
 * Features:
 * - Action categories with drag-and-drop reordering
 * - Connector suggestions (default + per-action overrides)
 * - Grammar rules management
 * - Team prefix strip list
 * - Copy-on-write: edits create company-specific override
 * - Reset to global defaults
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchCaptionBuilderConfig,
  updateCaptionBuilderConfig,
  resetCaptionBuilderConfig,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import TagInput from '../../../../shared/components/ui/TagInput/TagInput';
import styles from './CaptionBuilderSettings.module.css';

// ---------------------------------------------------------------------------
// Sortable action row
// ---------------------------------------------------------------------------

function SortableActionRow({ id, action, onUpdate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.actionRow}>
      <span className={styles.actionDragHandle} {...attributes} {...listeners}>
        {'::'}
      </span>
      <input
        className={styles.actionLabelInput}
        value={action.label}
        onChange={(e) => onUpdate({ ...action, label: e.target.value })}
        placeholder="Label"
      />
      <input
        className={styles.actionTemplateInput}
        value={action.template}
        onChange={(e) => onUpdate({ ...action, template: e.target.value })}
        placeholder="Template, e.g. scores {score}"
        title="Available: {score}, {team}, {team_abbr}"
      />
      <label className={styles.actionCheckbox}>
        <input
          type="checkbox"
          checked={action.needsScore || false}
          onChange={(e) => onUpdate({ ...action, needsScore: e.target.checked })}
        />
        Score
      </label>
      <div className={styles.actionKeywords}>
        <TagInput
          value={action.keywords || []}
          onChange={(kws) => onUpdate({ ...action, keywords: kws })}
          placeholder="Keywords..."
        />
      </div>
      <button className={styles.actionDelete} onClick={onDelete} title="Remove action">
        {'\u00d7'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable category card
// ---------------------------------------------------------------------------

function SortableCategoryCard({
  id,
  category,
  expanded,
  onToggle,
  onUpdateCategory,
  onDeleteCategory,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const actionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const actionIds = useMemo(
    () => category.actions.map((a) => a.id),
    [category.actions]
  );

  const handleActionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = category.actions.findIndex((a) => a.id === active.id);
    const newIndex = category.actions.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onUpdateCategory({
      ...category,
      actions: arrayMove(category.actions, oldIndex, newIndex),
    });
  };

  const updateAction = (index, updated) => {
    const newActions = [...category.actions];
    newActions[index] = updated;
    onUpdateCategory({ ...category, actions: newActions });
  };

  const deleteAction = (index) => {
    const newActions = category.actions.filter((_, i) => i !== index);
    onUpdateCategory({ ...category, actions: newActions });
  };

  const addAction = () => {
    const newId = `${category.id}_action_${Date.now()}`;
    onUpdateCategory({
      ...category,
      actions: [
        ...category.actions,
        { id: newId, label: '', template: '', needsScore: false, keywords: [] },
      ],
    });
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.categoryCard}>
      <div className={styles.categoryHeader} onClick={onToggle}>
        <span className={styles.categoryDragHandle} {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
          {':::'}
        </span>
        <input
          className={styles.categoryLabelInput}
          value={category.label || ''}
          onChange={(e) => {
            e.stopPropagation();
            onUpdateCategory({ ...category, label: e.target.value });
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder="Category name"
        />
        <button className={styles.categoryToggle} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          {expanded ? 'v' : '>'}
        </button>
        <button
          className={styles.categoryDelete}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteCategory();
          }}
          title="Remove category"
        >
          {'\u00d7'}
        </button>
      </div>
      {expanded && (
        <div className={styles.categoryBody}>
          <DndContext
            sensors={actionSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleActionDragEnd}
          >
            <SortableContext items={actionIds} strategy={verticalListSortingStrategy}>
              {category.actions.map((action, i) => (
                <SortableActionRow
                  key={action.id}
                  id={action.id}
                  action={action}
                  onUpdate={(updated) => updateAction(i, updated)}
                  onDelete={() => deleteAction(i)}
                />
              ))}
            </SortableContext>
          </DndContext>
          <button className={styles.addButton} onClick={addAction}>
            + Add action
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CaptionBuilderSettings() {
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showResetModal, setShowResetModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCaptionBuilderConfig();
      setConfig(data);
      setOriginalConfig(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // ----- Action categories -----

  const categoryIds = useMemo(
    () => (config?.action_categories || []).map((c) => c.id),
    [config?.action_categories]
  );

  const handleCategoryDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const cats = config.action_categories;
    const oldIndex = cats.findIndex((c) => c.id === active.id);
    const newIndex = cats.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setConfig((prev) => ({
      ...prev,
      action_categories: arrayMove(prev.action_categories, oldIndex, newIndex),
    }));
  };

  const updateCategory = (index, updated) => {
    setConfig((prev) => {
      const cats = [...prev.action_categories];
      cats[index] = updated;
      return { ...prev, action_categories: cats };
    });
  };

  const deleteCategory = (index) => {
    if (!confirm('Delete this category and all its actions?')) return;
    setConfig((prev) => ({
      ...prev,
      action_categories: prev.action_categories.filter((_, i) => i !== index),
    }));
  };

  const addCategory = () => {
    const newId = `cat_${Date.now()}`;
    setConfig((prev) => ({
      ...prev,
      action_categories: [
        ...(prev.action_categories || []),
        { id: newId, label: '', actions: [] },
      ],
    }));
    setExpandedCategories((prev) => new Set([...prev, newId]));
  };

  const toggleCategory = (catId) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  // ----- Connector suggestions -----

  const allActionIds = useMemo(() => {
    const ids = [];
    for (const cat of config?.action_categories || []) {
      for (const a of cat.actions) {
        if (a.id && a.label) ids.push({ id: a.id, label: a.label });
      }
    }
    return ids;
  }, [config?.action_categories]);

  const updateDefaultConnectors = (connectors) => {
    setConfig((prev) => ({
      ...prev,
      connector_suggestions: { ...prev.connector_suggestions, default: connectors },
    }));
  };

  const updateActionConnectors = (actionId, connectors) => {
    setConfig((prev) => ({
      ...prev,
      connector_suggestions: { ...prev.connector_suggestions, [actionId]: connectors },
    }));
  };

  const removeActionConnectorOverride = (actionId) => {
    setConfig((prev) => {
      const newSuggestions = { ...prev.connector_suggestions };
      delete newSuggestions[actionId];
      return { ...prev, connector_suggestions: newSuggestions };
    });
  };

  const [newOverrideAction, setNewOverrideAction] = useState('');

  const addConnectorOverride = () => {
    if (!newOverrideAction) return;
    setConfig((prev) => ({
      ...prev,
      connector_suggestions: {
        ...prev.connector_suggestions,
        [newOverrideAction]: [],
      },
    }));
    setNewOverrideAction('');
  };

  const connectorOverrideKeys = useMemo(() => {
    if (!config?.connector_suggestions) return [];
    return Object.keys(config.connector_suggestions).filter((k) => k !== 'default');
  }, [config?.connector_suggestions]);

  const availableForOverride = useMemo(() => {
    return allActionIds.filter(
      (a) => !connectorOverrideKeys.includes(a.id)
    );
  }, [allActionIds, connectorOverrideKeys]);

  // ----- Grammar rules -----

  const updateGrammarRule = (index, field, value) => {
    setConfig((prev) => {
      const rules = [...prev.grammar_rules];
      rules[index] = { ...rules[index], [field]: value };
      return { ...prev, grammar_rules: rules };
    });
  };

  const deleteGrammarRule = (index) => {
    setConfig((prev) => ({
      ...prev,
      grammar_rules: prev.grammar_rules.filter((_, i) => i !== index),
    }));
  };

  const addGrammarRule = () => {
    setConfig((prev) => ({
      ...prev,
      grammar_rules: [...(prev.grammar_rules || []), { pattern: '', message: '' }],
    }));
  };

  // ----- Team prefixes -----

  const updatePrefixes = (prefixes) => {
    setConfig((prev) => ({
      ...prev,
      team_prefix_strip: prefixes,
    }));
  };

  // ----- Save / Reset -----

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const result = await updateCaptionBuilderConfig({
        action_categories: config.action_categories,
        connector_suggestions: config.connector_suggestions,
        grammar_rules: config.grammar_rules,
        team_prefix_strip: config.team_prefix_strip,
      });
      setConfig(result);
      setOriginalConfig(result);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setShowResetModal(false);
    setSaving(true);
    setError(null);
    try {
      const result = await resetCaptionBuilderConfig();
      setConfig(result);
      setOriginalConfig(result);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const isDirty = useMemo(() => {
    if (!config || !originalConfig) return false;
    return JSON.stringify({
      action_categories: config.action_categories,
      connector_suggestions: config.connector_suggestions,
      grammar_rules: config.grammar_rules,
      team_prefix_strip: config.team_prefix_strip,
    }) !== JSON.stringify({
      action_categories: originalConfig.action_categories,
      connector_suggestions: originalConfig.connector_suggestions,
      grammar_rules: originalConfig.grammar_rules,
      team_prefix_strip: originalConfig.team_prefix_strip,
    });
  }, [config, originalConfig]);

  // ----- Render -----

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading caption builder config...</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={styles.error}>
        Failed to load caption builder configuration. Please try again.
      </div>
    );
  }

  const isOverride = config.is_company_override;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Caption Builder</h2>
          <p className={styles.subtitle}>
            Configure action templates, connectors, and grammar rules
          </p>
          <span className={`${styles.badge} ${isOverride ? styles.badgeCompany : styles.badgeGlobal}`}>
            {isOverride ? 'Company configuration' : 'Global defaults'}
          </span>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {success && (
        <div className={styles.successBanner}>
          Configuration saved successfully
        </div>
      )}

      <div className={styles.sections}>
        {/* Section 1: Action Categories */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Action Categories</h3>
          <p className={styles.sectionHint}>
            Drag to reorder categories and actions. Templates support: {'{'+'score}, {team}, {team_abbr}'}
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCategoryDragEnd}
          >
            <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
              {(config.action_categories || []).map((cat, i) => (
                <SortableCategoryCard
                  key={cat.id}
                  id={cat.id}
                  category={cat}
                  expanded={expandedCategories.has(cat.id)}
                  onToggle={() => toggleCategory(cat.id)}
                  onUpdateCategory={(updated) => updateCategory(i, updated)}
                  onDeleteCategory={() => deleteCategory(i)}
                />
              ))}
            </SortableContext>
          </DndContext>
          <button className={styles.addButton} onClick={addCategory} style={{ marginTop: 12 }}>
            + Add category
          </button>
        </section>

        {/* Section 2: Connector Suggestions */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Connector Suggestions</h3>
          <p className={styles.sectionHint}>
            Default connectors shown between persons in the chain. Per-action overrides replace defaults after specific actions.
          </p>

          <div className={styles.connectorRow}>
            <span className={styles.connectorLabel}>Default connectors</span>
            <TagInput
              value={config.connector_suggestions?.default || []}
              onChange={updateDefaultConnectors}
              placeholder="Add connector..."
            />
          </div>

          {connectorOverrideKeys.map((actionId) => {
            const actionInfo = allActionIds.find((a) => a.id === actionId);
            const label = actionInfo?.label || actionId;
            return (
              <div key={actionId} className={styles.connectorRow}>
                <span className={styles.connectorLabel}>After: {label}</span>
                <TagInput
                  value={config.connector_suggestions[actionId] || []}
                  onChange={(v) => updateActionConnectors(actionId, v)}
                  placeholder="Add connector..."
                />
                <button
                  className={styles.connectorDelete}
                  onClick={() => removeActionConnectorOverride(actionId)}
                  title="Remove override"
                >
                  {'\u00d7'}
                </button>
              </div>
            );
          })}

          {availableForOverride.length > 0 && (
            <div className={styles.connectorRow}>
              <select
                className={styles.overrideSelect}
                value={newOverrideAction}
                onChange={(e) => setNewOverrideAction(e.target.value)}
              >
                <option value="">Add action override...</option>
                {availableForOverride.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
              <Button variant="ghost" size="sm" onClick={addConnectorOverride} disabled={!newOverrideAction}>
                Add
              </Button>
            </div>
          )}
        </section>

        {/* Section 3: Grammar Rules */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Grammar Rules</h3>
          <p className={styles.sectionHint}>
            Regex patterns (case-insensitive) that trigger warnings in the caption preview.
          </p>

          {(config.grammar_rules || []).map((rule, i) => (
            <div key={i} className={styles.grammarRow}>
              <input
                className={styles.grammarPatternInput}
                value={rule.pattern}
                onChange={(e) => updateGrammarRule(i, 'pattern', e.target.value)}
                placeholder="Pattern (regex)"
              />
              <input
                className={styles.grammarMessageInput}
                value={rule.message}
                onChange={(e) => updateGrammarRule(i, 'message', e.target.value)}
                placeholder="Warning message"
              />
              <button
                className={styles.grammarDelete}
                onClick={() => deleteGrammarRule(i)}
                title="Remove rule"
              >
                {'\u00d7'}
              </button>
            </div>
          ))}
          <button className={styles.addButton} onClick={addGrammarRule}>
            + Add rule
          </button>
        </section>

        {/* Section 4: Team Prefixes */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Team Prefixes</h3>
          <p className={styles.sectionHint}>
            Prefixes stripped from team names to derive abbreviations. Used as fallback when teams don&apos;t have a short name set.
          </p>
          <TagInput
            value={config.team_prefix_strip || []}
            onChange={updatePrefixes}
            placeholder="Add prefix (e.g. FC, SC)..."
          />
        </section>
      </div>

      <div className={styles.footer}>
        <div>
          {isOverride && (
            <Button variant="ghost" onClick={() => setShowResetModal(true)} disabled={saving}>
              Reset to Defaults
            </Button>
          )}
        </div>
        <div className={styles.footerRight}>
          <Button variant="ghost" onClick={loadConfig} disabled={saving}>
            Discard
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Reset confirmation modal */}
      {showResetModal && (
        <Modal isOpen={true} onClose={() => setShowResetModal(false)}>
          <Modal.Header onClose={() => setShowResetModal(false)}>
            Reset to Global Defaults
          </Modal.Header>
          <Modal.Body>
            <p>
              This will delete your company-specific caption builder configuration
              and revert to the global defaults. This action cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onClick={() => setShowResetModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReset}>
              Reset
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
